/**
 * Tiny client-side check for "this question is clearly Spanish". Used to flip
 * the language option to Spanish BEFORE a turn is sent, so the same turn is
 * answered in Spanish. One direction only: there is no English detection, the
 * trader switches back with the Language option in the + menu.
 *
 * Conservative on purpose (a wrong flip changes every later answer): tickers,
 * numbers, URLs and a panel-mention preamble are ignored, and a turn needs a
 * minimum number of real words and a clear margin over English function words.
 * No model call, no dependency.
 */

/** Spanish function words that are not also common English words. */
const SPANISH_WORDS = new Set([
  'el',
  'de',
  'un',
  'la',
  'en',
  'los',
  'las',
  'una',
  'unos',
  'unas',
  'del',
  'que',
  'qué',
  'por',
  'para',
  'con',
  'es',
  'está',
  'están',
  'hay',
  'cómo',
  'como',
  'cuál',
  'cuáles',
  'cuánto',
  'cuánta',
  'cuántos',
  'cuántas',
  'cuándo',
  'dónde',
  'quién',
  'quiénes',
  'mi',
  'mis',
  'tu',
  'tus',
  'su',
  'sus',
  'se',
  'lo',
  'le',
  'les',
  'pero',
  'más',
  'muy',
  'también',
  'este',
  'esta',
  'esto',
  'estos',
  'estas',
  'ese',
  'esa',
  'hoy',
  'ayer',
  'acciones',
  'acción',
  'dame',
  'muéstrame',
  'porque',
  'cuando',
  'donde',
  'ha',
  'han',
  'fue',
  'son',
  'va',
  'van',
  'tiene',
  'tienen',
  'sobre',
  'entre',
  'después',
  'antes',
  'mas',
  'al',
  'y',
  'añade',
  'agrega',
  'cambia',
  'escribe',
  'escríbeme',
  'explícame',
  'dime',
  'quiero',
  'puedes',
  'otras',
  'otros',
  'todas',
  'todos',
])

/** English function words, weighed against the Spanish ones. */
const ENGLISH_WORDS = new Set([
  'the',
  'is',
  'are',
  'was',
  'were',
  'what',
  'why',
  'how',
  'when',
  'where',
  'which',
  'who',
  'of',
  'to',
  'in',
  'on',
  'for',
  'with',
  'and',
  'or',
  'my',
  'me',
  'you',
  'your',
  'it',
  'this',
  'that',
  'do',
  'does',
  'did',
  'can',
  'could',
  'show',
  'a',
  'an',
  'at',
  'by',
  'from',
  'has',
  'have',
  'had',
  'be',
  'not',
  'will',
  'would',
  'about',
  'all',
  'any',
  'i',
  'if',
  'than',
  'then',
  'there',
  'their',
  'they',
  'we',
  'its',
  'these',
  'those',
  'add',
  'give',
  'tell',
  'write',
  'mute',
])

const MIN_WORDS = 3
const MIN_SCORE = 2
const MIN_MARGIN = 2

const URL_RE = /\bhttps?:\/\/\S+|\bwww\.\S+/gi
const PREAMBLE_RE = /^\[The user is referring to these panel\(s\)[\s\S]*?\]\n\n/
const CASHTAG_RE = /\$[A-Za-z]{1,6}\b/g
const NUMBER_RE = /\d[\d.,]*%?/g
const WORD_RE = /\p{L}+/gu
const ACCENTED_RE = /[ñáéíóú]/i
/** Letters Spanish never uses: Italian, German, French or Portuguese, not Spanish. */
const OTHER_LANGUAGE_RE = /[àèìòùäöüßçãõâêôœ]/i

/** Words that count for Spanish only on their own accent or a second signal. */
const AMBIGUOUS = new Set([
  'son',
  'ha',
  'va',
  'y',
  'mi',
  'lo',
  'le',
  'se',
  'su',
  'tu',
  'es',
  'al',
  'con',
  'como',
  'mas',
  'la',
  'en',
  'un',
  'de',
])

export function isClearlySpanish(text: string): boolean {
  const cleaned = text
    .replace(PREAMBLE_RE, '')
    .replace(URL_RE, ' ')
    .replace(CASHTAG_RE, ' ')
    .replace(NUMBER_RE, ' ')
  const raw = cleaned.match(WORD_RE) ?? []
  // All-caps tokens are tickers or acronyms (RVOL, TNON), not language.
  const words = raw.filter(
    (w) => !(w.length >= 2 && w === w.toUpperCase() && w !== w.toLowerCase()),
  )
  if (OTHER_LANGUAGE_RE.test(cleaned)) return false
  const marks = /[¿¡]/.test(cleaned) ? 1 : 0
  if (words.length < MIN_WORDS && !(marks && words.length >= 2)) return false

  let es = marks * 3
  // A flip needs an unambiguous Spanish function word (or ¿/¡), not accents alone.
  let anchored = marks > 0
  let en = 0
  for (const word of words) {
    const w = word.toLowerCase()
    // An accent is weak evidence (café, Nestlé): worth less than a function word.
    if (ACCENTED_RE.test(w)) es += 1
    if (SPANISH_WORDS.has(w)) {
      if (AMBIGUOUS.has(w)) es += 0.5
      else {
        es += 1
        anchored = true
      }
    }
    if (ENGLISH_WORDS.has(w)) en += 1
  }
  return anchored && es >= MIN_SCORE && es - en >= MIN_MARGIN
}
